import React, { useState } from "react"
import { Button, Checkbox, Col, DatePicker, Flex, Form, Input, InputNumber, Row, Switch, Table, TimePicker, Tooltip } from "antd"
import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons"
import classNames from "classnames"
import { useTranslation } from "react-i18next"
import { Card } from "ui/Card"
import { Icon } from "ui/Icon"
import { Text } from "ui/Typography"
import styles from "./SeasonalAvailability.module.css"

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

const DUMMY_TICKET_TYPES = [
  { id: 1, title: "Adults" },
  { id: 2, title: "Children" },
  { id: 3, title: "VIP" }
]

const generateId = () => Date.now() + Math.random()

const defaultTimeSlot = () => ({ id: generateId(), start: null, end: null })

const SeasonEditForm = ({ season, ticketTypes, onSave, onCancel, onChange }) => {
  const { t } = useTranslation()

  const handleDayToggle = (dayIndex) => {
    const days = season.days.includes(dayIndex) ? season.days.filter((d) => d !== dayIndex) : [...season.days, dayIndex]
    onChange({ ...season, days })
  }

  const handleAddTimeSlot = () => {
    onChange({ ...season, timeSlots: [...season.timeSlots, defaultTimeSlot()] })
  }

  const handleTimeSlotChange = (slotId, field, value) => {
    onChange({
      ...season,
      timeSlots: season.timeSlots.map((s) => (s.id === slotId ? { ...s, [field]: value } : s))
    })
  }

  const handleTicketPriceChange = (ticketId, price) => {
    const existing = season.ticketPrices.find((tp) => tp.ticketId === ticketId)
    const updated = existing
      ? season.ticketPrices.map((tp) => (tp.ticketId === ticketId ? { ...tp, price } : tp))
      : [...season.ticketPrices, { ticketId, price, enabled: true }]
    onChange({ ...season, ticketPrices: updated })
  }

  const handleTicketToggle = (ticketId, enabled) => {
    const existing = season.ticketPrices.find((tp) => tp.ticketId === ticketId)
    const updated = existing
      ? season.ticketPrices.map((tp) => (tp.ticketId === ticketId ? { ...tp, enabled } : tp))
      : [...season.ticketPrices, { ticketId, price: null, enabled }]
    onChange({ ...season, ticketPrices: updated })
  }

  const getTicketPrice = (ticketId) => season.ticketPrices.find((tp) => tp.ticketId === ticketId)

  return (
    <div vertical gap={16} className={styles.formWrapper}>
      <Form layout="vertical" className="pb-0 border-b">
        <Form.Item label={t("experiences.seasonal.name_label")} className="mb-none">
          <Input
            className="ant-input ant-input-lg"
            value={season.name}
            onChange={(e) => onChange({ ...season, name: e.target.value })}
            placeholder={t("experiences.seasonal.name_placeholder")}
          />
        </Form.Item>

        <Row gutter={16} className="mt-m">
          <Col span={12}>
            <Form.Item label={t("experiences.seasonal.start_date_label")} className="mb-none">
              <DatePicker
                value={season.startDate}
                onChange={(val) => onChange({ ...season, startDate: val })}
                style={{ width: "100%" }}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t("experiences.seasonal.end_date_label")} className="mb-none">
              <DatePicker
                value={season.endDate}
                onChange={(val) => onChange({ ...season, endDate: val })}
                style={{ width: "100%" }}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Flex vertical gap={8}>
        <Text className={styles.timeSlotsLabel}>{t("experiences.seasonal.time_slots_label")}</Text>
        {season.timeSlots.map((slot) => (
          <Row key={slot.id} gutter={16}>
            <Col span={12}>
              <Text className={styles.slotFieldLabel}>{t("experiences.seasonal.start_time_label")}</Text>
              <TimePicker
                needConfirm={false}
                value={slot.start}
                onChange={(val) => handleTimeSlotChange(slot.id, "start", val)}
                format="hh:mm A"
                use12Hours
                style={{ width: "100%" }}
                size="large"
              />
            </Col>
            <Col span={12}>
              <Text className={styles.slotFieldLabel}>{t("experiences.seasonal.end_time_label")}</Text>
              <TimePicker
                needConfirm={false}
                value={slot.end}
                onChange={(val) => handleTimeSlotChange(slot.id, "end", val)}
                format="hh:mm A"
                use12Hours
                style={{ width: "100%" }}
                size="large"
              />
            </Col>
          </Row>
        ))}
        <Button type="link" icon={<PlusOutlined />} onClick={handleAddTimeSlot} className={styles.addTimeslotBtn}>
          {t("experiences.seasonal.add_timeslot_button")}
        </Button>
      </Flex>

      <div className={`border-b pb-xl ${styles.daySelector}`}>
        {DAYS.map((day, index) => (
          <div key={day} className="flex-column align-center">
            <div
              className={classNames(styles.dayItem, season.days.includes(index) && styles.dayItemSelected)}
              onClick={() => handleDayToggle(index)}
            >
              <Text variant="sub">{day}</Text>
            </div>
            <Checkbox
              checked={season.days.includes(index)}
              onChange={() => handleDayToggle(index)}
              className={styles.dayCheckbox}
            />
          </div>
        ))}
      </div>

      {ticketTypes.length > 0 && (
        <Flex vertical>
          <Text className={styles.ticketTypesLabel}>{t("experiences.seasonal.ticket_types_label")}</Text>
          {ticketTypes.map((ticket) => {
            const tp = getTicketPrice(ticket.id)
            return (
              <Flex key={ticket.id} align="center" className={styles.ticketRow}>
                <Flex align="center" gap={8} flex={1}>
                  <Switch checked={tp?.enabled !== false} onChange={(checked) => handleTicketToggle(ticket.id, checked)} />
                  <Text className={styles.ticketTitle}>{ticket.title}</Text>
                  <Tooltip title={t("experiences.seasonal.pricing_info_tooltip", { ticket: ticket.title })}>
                    <InfoCircleOutlined className={styles.infoIcon} />
                  </Tooltip>
                </Flex>
                <Flex align="center" gap={8}>
                  <Text className={styles.priceLabel}>{t("experiences.seasonal.price_label")}</Text>
                  <InputNumber
                    value={tp?.price ?? 0}
                    onChange={(val) => handleTicketPriceChange(ticket.id, val)}
                    prefix="$"
                    min={0}
                    precision={2}
                    className={styles.priceInput}
                    size="middle"
                  />
                </Flex>
              </Flex>
            )
          })}
        </Flex>
      )}

      <Flex justify="space-between" className={styles.formFooter}>
        <Flex gap={8}>
          <Button color="primary" variant="outlined" size="large" onClick={onCancel}>
            {t("global.cancel")}
          </Button>
          <Button color="primary" variant="solid" size="large" onClick={onSave} disabled={!season.name}>
            {t("global.save")}
          </Button>
        </Flex>
        <Button color="danger" variant="solid" size="large" onClick={onCancel}>
          {t("global.delete")}
        </Button>
      </Flex>
    </div>
  )
}

export const SeasonalAvailability = ({ ticketTypes = DUMMY_TICKET_TYPES }) => {
  const { t } = useTranslation()
  const [seasons, setSeasons] = useState([])
  const [expandedKeys, setExpandedKeys] = useState([])

  const handleAddNew = () => {
    const newSeason = {
      id: generateId(),
      name: "",
      startDate: null,
      endDate: null,
      timeSlots: [defaultTimeSlot()],
      days: [],
      ticketPrices: []
    }
    setSeasons((prev) => [...prev, newSeason])
    setExpandedKeys([newSeason.id])
  }

  const handleChange = (id, updated) => {
    setSeasons((prev) => prev.map((s) => (s.id === id ? updated : s)))
  }

  const handleSave = () => {
    setExpandedKeys([])
  }

  const handleCancel = () => {
    setExpandedKeys([])
  }

  const columns = [
    {
      title: t("experiences.seasonal.name_column"),
      dataIndex: "name",
      key: "name",
      render: (name) => <Text strong>{name || "-"}</Text>
    },
    {
      title: t("experiences.seasonal.start_date_column"),
      dataIndex: "startDate",
      key: "startDate",
      render: (date) => (
        <Text className={styles.seasonDate} strong>
          {date ? date.format("MM/DD/YYYY") : "-"}
        </Text>
      )
    },
    {
      title: t("experiences.seasonal.end_date_column"),
      dataIndex: "endDate",
      key: "endDate",
      render: (date) => (
        <Text className={styles.seasonDate} strong>
          {date ? date.format("MM/DD/YYYY") : "-"}
        </Text>
      )
    }
  ]

  return (
    <Card
      title={
        <Flex align="center" gap={6}>
          {t("experiences.seasonal.settings_title")}
          <Tooltip placement="right" title={t("experiences.seasonal.settings_tooltip")}>
            <InfoCircleOutlined className={styles.infoIcon} />
          </Tooltip>
        </Flex>
      }
      className="mb-xl"
      extra={
        <Button size="large" type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>
          {t("experiences.seasonal.add_new_button")}
        </Button>
      }
    >
      {seasons.length > 0 && (
        <div className={styles.responsiveTableContainer}>
          <Table
            rowKey="id"
            dataSource={seasons}
            columns={columns}
            pagination={false}
            rowHoverable={false}
            size="middle"
            className={styles.customTable}
            rowClassName={styles.customTableRow}
            expandable={{
              expandedRowKeys: expandedKeys,
              expandIconColumnIndex: 3,
              rowExpandable: () => true,
              expandedRowClassName: () => styles.expandedRow,
              expandIcon: ({ expanded, onExpand, record }) => (
                <Icon
                  type="feather"
                  name={expanded ? "ChevronUp" : "ChevronDown"}
                  color="currentColor"
                  width={20}
                  height={20}
                  onClick={(e) => onExpand(record, e)}
                />
              ),
              onExpand: (expanded, record) => setExpandedKeys(expanded ? [record.id] : []),
              expandedRowRender: (record) => (
                <SeasonEditForm
                  season={record}
                  ticketTypes={ticketTypes}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  onChange={(updated) => handleChange(record.id, updated)}
                />
              )
            }}
          />
        </div>
      )}
    </Card>
  )
}
